import { createContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react'
import { AuthContextType, User } from '../types/auth'
import { authService } from '../services/authService'
import { decodeToken, isTokenExpired } from '../utils/jwt'

const TOKEN_KEY = 'kaab_admin_token'
const REFRESH_TOKEN_KEY = 'kaab_admin_refresh_token'
const USER_KEY = 'kaab_admin_user'

function saveSession(token: string, refreshToken: string, user: User) {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

function loadSession(): { token: string; user: User } | null {
  const token = localStorage.getItem(TOKEN_KEY)
  const raw = localStorage.getItem(USER_KEY)
  if (!token || !raw) return null
  // Allow slightly-expired tokens — apiClient will refresh on first 401
  if (isTokenExpired(token) && !localStorage.getItem(REFRESH_TOKEN_KEY)) {
    clearSession()
    return null
  }
  try {
    return { token, user: JSON.parse(raw) as User }
  } catch {
    clearSession()
    return null
  }
}

export const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => loadSession()?.token ?? null)
  const [user, setUser] = useState<User | null>(() => loadSession()?.user ?? null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const handleExpired = () => {
      clearSession()
      setToken(null)
      setUser(null)
    }
    const handleRefreshed = (e: Event) => {
      const { detail } = e as CustomEvent<{ token: string }>
      setToken(detail.token)
    }
    window.addEventListener('auth:expired', handleExpired)
    window.addEventListener('auth:refreshed', handleRefreshed)
    return () => {
      window.removeEventListener('auth:expired', handleExpired)
      window.removeEventListener('auth:refreshed', handleRefreshed)
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true)
    try {
      const response = await authService.login(email, password)
      const decoded = decodeToken(response.token)
      if (!decoded) throw new Error('Invalid token')
      const userData: User = {
        id: '',
        email: decoded.username,
        name: null,
        locale: 'en',
        roles: decoded.roles ?? [],
      }
      saveSession(response.token, response.refresh_token, userData)
      setToken(response.token)
      setUser(userData)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    clearSession()
    setToken(null)
    setUser(null)
  }, [])

  const hasRole = useCallback(
    (role: string) => {
      if (!user) return false
      if (user.roles.includes('ROLE_ADMIN')) return true
      return user.roles.includes(role)
    },
    [user]
  )

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      token,
      login,
      logout,
      isAuthenticated: token !== null,
      isLoading,
      hasRole,
    }),
    [user, token, login, logout, isLoading, hasRole]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
